import { useEffect } from "react";
import { BrowserRouter, Link, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { PreviewBanner } from "./components/Chrome";
import { ComingSoon } from "./components/ComingSoon";
import PeriodDetail from "./pages/admin/pay/PeriodDetail";
import AdminPay from "./pages/admin/pay/AdminPay";
import AdminAdministrators from "./pages/admin/settings/AdminAdministrators";
import AdminElections from "./pages/admin/elections/AdminElections";
import AdminPayments from "./pages/admin/payments/AdminPayments";
import AdminReports from "./pages/admin/reports/AdminReports";
import LaunchReadiness from "./pages/admin/settings/LaunchReadiness";
import BrandingFiles from "./pages/admin/settings/BrandingFiles";
import MySecurity from "./pages/admin/settings/MySecurity";
import AdminElectionDetail from "./pages/admin/elections/AdminElectionDetail";
import MyPaymentMethod from "./pages/employee/MyPaymentMethod";
import { RequireRole } from "./components/RouteGuards";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminEmployees from "./pages/admin/employees/AdminEmployees";
import EmployeeDetailPage from "./pages/admin/employees/EmployeeDetailPage";
import Register from "./pages/Register";
import AdminLayout, { ADMIN_COMING_SOON } from "./pages/admin/AdminLayout";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminVenues from "./pages/admin/venues/AdminVenues";
import VenueFormPage from "./pages/admin/venues/VenueFormPage";
import EmployeeLayout, { EMPLOYEE_NAV } from "./pages/employee/EmployeeLayout";
import EmployeeLogin from "./pages/employee/EmployeeLogin";
import MyProfile from "./pages/employee/MyProfile";
import MyAssignments from "./pages/employee/MyAssignments";
import AdminShifts from "./pages/admin/shifts/AdminShifts";
import AdminTemplates from "./pages/admin/templates/AdminTemplates";
import AdminSmsActivity from "./pages/admin/sms/AdminSmsActivity";
import MockOutbox from "./pages/admin/sms/MockOutbox";
import AdminSettings from "./pages/admin/settings/AdminSettings";
import Landing from "./pages/Landing";
import PunchPage from "./pages/punch/PunchPage";
import CorrectionPage from "./pages/punch/CorrectionPage";
import AdminTimeclock from "./pages/admin/timeclock/AdminTimeclock";
import AdminReview from "./pages/admin/review/AdminReview";
import MyClock from "./pages/employee/MyClock";
import MyTimesheets from "./pages/employee/MyTimesheets";
import AdminTimesheets from "./pages/admin/sessions/AdminTimesheets";
import MyHelp from "./pages/employee/MyHelp";
import SmsProgram from "./pages/public/SmsProgram";
import TextGuidePage from "./pages/public/journey/ShiftJourneyPage";
import Privacy from "./pages/public/Privacy";
import Terms from "./pages/public/Terms";
import PublicHelp from "./pages/public/PublicHelp";
import TwilioGuide from "./pages/admin/settings/TwilioGuide";
import TwilioConnection from "./pages/admin/settings/TwilioConnection";
import EmailSettings from "./pages/admin/settings/EmailSettings";
import { BrandStatusProvider } from "./components/BrandStatus";

function BannerHeightSync() {
  const { config } = useAuth();
  useEffect(() => {
    document.documentElement.style.setProperty("--banner-h", config?.is_preview ? "24px" : "0px");
  }, [config]);
  return null;
}

function NotFound() {
  return (
    <div data-testid="not-found-page" className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
      <p className="font-display text-6xl font-extrabold text-white">404</p>
      <Link to="/" data-testid="not-found-home-link" className="text-sm text-signal hover:text-white">Back to home</Link>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrandStatusProvider>
      <BrowserRouter>
        <BannerHeightSync />
        <PreviewBanner />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<EmployeeLogin />} />
          <Route path="/sms" element={<SmsProgram />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/help" element={<PublicHelp />} />
          <Route path="/text" element={<TextGuidePage />} />
          <Route path="/register" element={<Register />} />
          <Route path="/p/:token" element={<PunchPage />} />
          <Route path="/c/:token" element={<CorrectionPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<RequireRole role="ADMIN"><AdminLayout /></RequireRole>}>
            <Route index element={<AdminDashboard />} />
            <Route path="employees" element={<AdminEmployees />} />
            <Route path="employees/:employeeId" element={<EmployeeDetailPage />} />
            <Route path="venues" element={<AdminVenues />} />
            <Route path="venues/new" element={<VenueFormPage />} />
            <Route path="venues/:venueId" element={<VenueFormPage />} />
            <Route path="shift-assignments" element={<AdminShifts />} />
            <Route path="templates" element={<AdminTemplates />} />
            <Route path="sms-activity" element={<AdminSmsActivity />} />
            <Route path="sms-activity/outbox" element={<MockOutbox />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="settings/twilio-guide" element={<TwilioGuide />} />
            <Route path="settings/twilio" element={<TwilioConnection />} />
            <Route path="settings/email" element={<EmailSettings />} />
            <Route path="settings/administrators" element={<AdminAdministrators />} />
            <Route path="payment-elections" element={<AdminElections />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="settings/launch-readiness" element={<LaunchReadiness />} />
            <Route path="settings/branding" element={<BrandingFiles />} />
            <Route path="settings/security" element={<MySecurity />} />
            <Route path="payment-elections/:employeeId" element={<AdminElectionDetail />} />
            <Route path="timeclock-activity" element={<AdminTimeclock />} />
            <Route path="corrections" element={<AdminReview />} />
            <Route path="timesheets" element={<AdminTimesheets />} />
            <Route path="pay-periods" element={<AdminPay />} />
            <Route path="pay-periods/:periodId" element={<PeriodDetail />} />
            {ADMIN_COMING_SOON.map((n) => (
              <Route key={n.slug} path={n.to.replace("/admin/", "")} element={<ComingSoon title={n.label} slug={n.slug} />} />
            ))}
          </Route>
          <Route path="/me" element={<RequireRole role="EMPLOYEE"><EmployeeLayout /></RequireRole>}>
            <Route index element={<MyClock />} />
            <Route path="profile" element={<MyProfile />} />
            <Route path="assignments" element={<MyAssignments />} />
            <Route path="help" element={<MyHelp />} />
            <Route path="payment-method" element={<MyPaymentMethod />} />
            <Route path="timesheets" element={<MyTimesheets />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      </BrandStatusProvider>
      <Toaster theme="dark" position="top-right" toastOptions={{ className: "rounded-[14px] border border-white/10" }} />
    </AuthProvider>
  );
}
